import React, { type JSX, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@core-ui/Components/ui/dialog.tsx';
import { cn } from '@core-ui-lib/utils.ts';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Wifi, AlertCircle, Volume2, InfoIcon } from 'lucide-react';
import { type StepStatus } from '@types';
import { useTestInternetViewModel } from '@utils/testInternetViewModel.ts';
import { useAudioTest } from '@utils/testSoundViewModel.ts';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@core-ui/Components/ui/tooltip.tsx';
import WaveMini from '@features/voicemail/components/WaveMini.tsx';
import { IconInfoCircle } from '@tabler/icons-react';
import { Label } from '@core-ui/Components/ui/label.tsx';

const TOOLTIP_DELAY_MS = 200;

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  isInternetTest: boolean;
}

export default function TestSoundInternetDialog ({
  open, setOpen, isInternetTest
}: Props): React.JSX.Element {

  const { t } = useTranslation();

  const {
    turnUdpStatus,
    turnUdpValue,
    udpError,
    turnTcpStatus,
    turnTcpValue,
    tcpError,
    bandwidthStatus,
    bandwidthValue,
    startAutoTest,
  } = useTestInternetViewModel();

  const {
    isPlaying,
    progress,
    startAudioTest,
    stopAudioTest,
  } = useAudioTest();

  useEffect(() => {
    if (!open) {
      stopAudioTest();
      return;
    }

    if (isInternetTest) {
      void startAutoTest();
    } else {
      const timer = setTimeout(() => {
        startAudioTest();
      }, 100);

      return () => {
        clearTimeout(timer);
        stopAudioTest();
      };
    }
  }, [open, isInternetTest]);

  const handleClose = (): void => {
    stopAudioTest();
    setOpen(false);
  };

  const getTranslatedValue = (value: string): string => {
    if (value.toLowerCase() === 'running') {
      return t('agentLayout.testDialog.internetMode.connectionStatuses.running');
    }
    if (value.toLowerCase() === 'blocked') {
      return t('agentLayout.testDialog.internetMode.connectionStatuses.blocked');
    }
    return value;
  };

  const renderRowIcon = (status: StepStatus): JSX.Element => {
    if (status === 'success') {
      return (
        <div className="h-5 w-5 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
          <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" strokeWidth={3} />
        </div>
      );
    }
    if (status === 'running') {
      return (
        <div className="h-5 w-5 flex items-center justify-center shrink-0">
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
        </div>
      );
    }
    if (status === 'failed') {
      return (
        <div className="h-5 w-5 rounded-full bg-destructive/20 flex items-center justify-center shrink-0">
          <AlertCircle className="h-3 w-3 text-destructive" />
        </div>
      );
    }
    return (
      <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center shrink-0" />
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          stopAudioTest();
        }
        setOpen(isOpen);
      }}>
      <DialogContent
        aria-describedby="dialog-description"
        data-state={undefined}
        className={cn(
          'rounded-3xl shadow-2xl w-full max-w-md p-6 gap-0',
          'bg-card text-foreground border border-border/60',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
        )}
      >
        <DialogHeader className="flex flex-row items-start gap-4 space-y-0 mb-6">
          {isInternetTest ? (
            <>
              <div className="h-12 w-12 rounded-full bg-sky-500/10 dark:bg-sky-500/20 flex items-center justify-center shrink-0 mt-0.5">
                <Wifi className="h-6 w-6 text-sky-500" />
              </div>
              <div className="flex flex-col text-left">
                <DialogTitle className="text-lg font-bold tracking-tight text-foreground">
                  {t('agentLayout.testDialog.internetMode.connectionModalTitle')}
                </DialogTitle>
                <DialogDescription
                  id="dialog-description"
                  className="text-sm text-muted-foreground mt-0.5 font-normal">
                  {t('agentLayout.testDialog.internetMode.connectionModalDesc')}
                </DialogDescription>
              </div>
            </>
          ) : (
            <>
              <div className="h-12 w-12 rounded-full bg-sky-500/10 dark:bg-sky-500/20 flex items-center justify-center shrink-0 mt-0.5">
                <Volume2 className="h-6 w-6 text-sky-500" />
              </div>
              <div className="flex flex-col text-left">
                <DialogTitle className="text-lg font-bold tracking-tight text-foreground">
                  {t('agentLayout.testDialog.soundMode.soundModalTitle')}
                </DialogTitle>
                <DialogDescription
                  id="dialog-description"
                  className="text-sm text-muted-foreground mt-0.5 font-normal">
                  {t('agentLayout.testDialog.soundMode.description')}
                </DialogDescription>
              </div>
            </>
          )}
        </DialogHeader>

        {isInternetTest ? (
          <div className="space-y-3">
            <span className="text-[11px] font-bold tracking-wider text-muted-foreground block px-1">
              {t('agentLayout.testDialog.internetMode.connectSubtitle')}
            </span>

            {/* UDP */}
            <div className="flex flex-col gap-1 border border-border/50 rounded-xl p-3 bg-muted/30">
              <div className="flex items-center justify-between h-6 w-full">
                <div className="flex items-center gap-3">
                  {renderRowIcon(turnUdpStatus)}
                  <span className="text-sm font-semibold text-foreground/90">{t('agentLayout.testDialog.internetMode.udpLabel')}</span>
                  <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center justify-center cursor-pointer">
                          <InfoIcon className='h-4 w-4 text-muted-foreground/90' />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" sideOffset={4}>
                        <p>{t('agentLayout.testDialog.internetMode.udpInfo')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <span className={cn(
                  'text-xs font-bold tracking-wide',
                  turnUdpStatus === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                )}>
                  {getTranslatedValue(turnUdpValue)}
                </span>
              </div>
              {turnUdpStatus === 'failed' && (udpError != null) && (
                <span className="text-[10px] text-destructive font-mono text-left pl-8 block mt-1 animate-in fade-in-50 duration-200">
                  {udpError}
                </span>
              )}
            </div>

            {/* TCP */}
            <div className="flex flex-col gap-1 border border-border/50 rounded-xl p-3 bg-muted/30">
              <div className="flex items-center justify-between h-6 w-full">
                <div className="flex items-center gap-3">
                  {renderRowIcon(turnTcpStatus)}
                  <span className="text-sm font-semibold text-foreground/90">{t('agentLayout.testDialog.internetMode.tcpLabel')}</span>
                  <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center justify-center cursor-pointer">
                          <InfoIcon className='h-4 w-4 text-muted-foreground/90' />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" sideOffset={4}>
                        <p>{t('agentLayout.testDialog.internetMode.tcpInfo')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <span className={cn(
                  'text-xs font-bold tracking-wide',
                  turnTcpStatus === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'
                )}>
                  {getTranslatedValue(turnTcpValue)}
                </span>
              </div>
              {turnTcpStatus === 'failed' && (tcpError != null) && (
                <span className="text-[10px] text-destructive font-mono text-left pl-8 block mt-1 animate-in fade-in-50 duration-200">
                  {tcpError}
                </span>
              )}
            </div>

            {/* Bandwidth */}
            <div className="flex flex-col gap-1 border border-border/50 rounded-xl p-3 bg-muted/30">
              <div className="flex items-center justify-between h-6 w-full">
                <div className="flex items-center gap-3">
                  {renderRowIcon(bandwidthStatus)}
                  <span className="text-sm font-semibold text-foreground/90">{t('agentLayout.testDialog.internetMode.bandwidthLabel')}</span>
                  <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center justify-center cursor-pointer">
                          <InfoIcon className='h-4 w-4 text-muted-foreground/90' />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" sideOffset={4}>
                        <p>{t('agentLayout.testDialog.internetMode.bandwidthInfo')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <span className={cn(
                  'text-xs font-bold tracking-wide',
                  bandwidthStatus === 'success' ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/70'
                )}>
                  {getTranslatedValue(bandwidthValue)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-5 flex flex-col w-full">
            <div className="p-1 mb-5 mt-1 w-full rounded-lg bg-muted border border-border flex flex-col md:flex-row items-center gap-3 ">
              <IconInfoCircle className="min-w-[20px] min-h-[20px]" />
              <Label className="text-sm text-center md:text-left">{t('agentLayout.testDialog.soundMode.info')}</Label>
            </div>
            <div className="flex flex-col items-center justify-center">
              <WaveMini progress={isPlaying ? progress : 0} animated={true}/>
            </div>

            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={handleClose}
                className="bg-primary hover:bg-primary/70 dark:hover:bg-primary/40 text-white font-semibold rounded-xl h-10 flex items-center justify-center w-1/3"
              >
                {t('agentLayout.testDialog.soundMode.noButton')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}